import { v4 as uuidv4 } from 'uuid';
import {
  AppError,
  DownloadOptions,
  DownloadResult,
  HttpMethod,
  RequestOptions,
  StreamOptions,
  StreamResult,
  TransportResponse,
  UploadOptions,
  UploadPayload,
} from './types';
import { NetworkPort, RawHttpRequest, RawHttpResponse, SessionPort } from './ports';

export interface HttpTransportConfig {
  readonly networkPort: NetworkPort;
  readonly sessionPort: SessionPort;
  readonly max5xxRetries?: number;
  readonly defaultTimeoutMs?: number;
}

export class HttpTransport {
  private networkPort: NetworkPort;
  private sessionPort: SessionPort;
  private max5xxRetries: number;
  private defaultTimeoutMs: number;

  // Single-flight token refresh mutex promise
  private activeRefreshPromise: Promise<string | null> | null = null;

  constructor(config: HttpTransportConfig) {
    this.networkPort = config.networkPort;
    this.sessionPort = config.sessionPort;
    this.max5xxRetries = config.max5xxRetries ?? 3;
    this.defaultTimeoutMs = config.defaultTimeoutMs ?? 30000;
  }

  /**
   * Primary entry point: executes a remote request across the network seam,
   * handling authentication, tenant routing, 5xx retries, and data extraction.
   */
  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.executeWithRetryAndAuth<T>(path, options, false);
    return response.data;
  }

  // --- Convenience Semantic Methods ---

  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  // --- High-Leverage Specialized Transfers ---

  /**
   * Handles multipart serialization, boundary headers, and upload progress tracking.
   */
  async upload<T>(path: string, payload: UploadPayload, options: UploadOptions = {}): Promise<T> {
    let formData: FormData;

    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      formData = payload;
    } else {
      formData = new FormData();
      if (typeof File !== 'undefined' && payload instanceof File) {
        formData.append('file', payload, payload.name);
      } else if (typeof Blob !== 'undefined' && payload instanceof Blob) {
        formData.append('file', payload, 'upload.bin');
      } else if (payload && typeof payload === 'object') {
        Object.entries(payload).forEach(([key, value]) => {
          if (value instanceof File) {
            formData.append(key, value, value.name);
          } else if (value instanceof Blob) {
            formData.append(key, value, 'blob.bin');
          } else if (Array.isArray(value)) {
            value.forEach((item, index) => {
              if (item instanceof File) {
                formData.append(`${key}[${index}]`, item, item.name);
              } else {
                formData.append(`${key}[${index}]`, String(item));
              }
            });
          } else if (value !== undefined && value !== null) {
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
          }
        });
      }
    }

    const response = await this.executeWithRetryAndAuth<T>(
      path,
      {
        ...options,
        method: options.method || 'POST',
        body: formData,
      },
      true // isUpload
    );

    return response.data;
  }

  /**
   * Handles binary file download, Content-Disposition header decoding, and browser saving.
   */
  async download(path: string, options: DownloadOptions = {}): Promise<DownloadResult> {
    const rawResponse = await this.executeRaw(path, {
      ...options,
      method: options.method || 'GET',
      responseType: 'blob',
    });

    const blob = rawResponse.body as Blob;

    if (rawResponse.status >= 400) {
      let errorBody: unknown = null;
      try {
        errorBody = JSON.parse(await blob.text());
      } catch {
        // non-JSON error body
      }
      throw this.normalizeError({
        status: rawResponse.status,
        body: errorBody,
        headers: rawResponse.headers,
      });
    }

    const contentDisposition = rawResponse.headers['content-disposition'] || '';
    let filename = 'download';

    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '').trim();
    }

    const contentType = rawResponse.headers['content-type'] || 'application/octet-stream';
    const size = blob?.size ?? 0;

    return {
      blob,
      filename,
      contentType,
      size,
      saveAs: (suggestedFilename?: string) => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        const nameToUse = suggestedFilename || filename;
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = nameToUse;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      },
    };
  }

  /**
   * Handles chunked streaming, Server-Sent Events, and NDJSON streaming readers.
   */
  async stream(path: string, options: StreamOptions = {}): Promise<StreamResult> {
    const rawResponse = await this.executeRaw(path, {
      ...options,
      method: options.method || 'GET',
      responseType: 'stream',
    });

    const stream = rawResponse.body as ReadableStream<Uint8Array>;
    if (!stream || typeof stream.getReader !== 'function') {
      throw this.normalizeError({
        status: rawResponse.status,
        message: 'Response body is not a readable stream',
        headers: rawResponse.headers,
      });
    }

    return {
      stream,
      textStream: async function* () {
        const reader = stream.getReader();
        const decoder = new TextDecoder('utf-8');
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) {
              yield decoder.decode(value, { stream: true });
            }
          }
        } finally {
          reader.releaseLock();
        }
      },
      jsonLines: async function* <TLine = unknown>() {
        const reader = stream.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              if (buffer.trim()) {
                try {
                  yield JSON.parse(buffer.trim()) as TLine;
                } catch {
                  // Ignore trailing invalid line
                }
              }
              break;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              const trimmed = line.trim();
              if (trimmed) {
                yield JSON.parse(trimmed) as TLine;
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      },
      cancel: async () => {
        await stream.cancel();
      },
    };
  }

  // --- Internal Core Execution Engine ---

  private async executeWithRetryAndAuth<T>(
    path: string,
    options: RequestOptions,
    isUpload: boolean
  ): Promise<TransportResponse<T>> {
    // Generate or preserve Idempotency-Key across retries/replays for mutating ops
    const method = options.method || 'GET';
    const isMutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    let idempotencyKey: string | undefined;

    if (options.idempotencyKey === true || (isMutating && options.idempotencyKey !== false)) {
      idempotencyKey = typeof options.idempotencyKey === 'string' ? options.idempotencyKey : uuidv4();
    }

    const effectiveOptions: RequestOptions = {
      ...options,
      idempotencyKey,
    };

    let attempt = 0;

    while (true) {
      attempt++;
      try {
        const rawResponse = await this.executeRaw(path, effectiveOptions);

        // Check for HTTP 401 Unauthorized
        if (rawResponse.status === 401) {
          const isAuthOrRefresh =
            path.includes('/auth/login') ||
            path.includes('/auth/refresh') ||
            path.includes('/platform/auth/login');

          if (isAuthOrRefresh || effectiveOptions.skipAuth) {
            throw this.normalizeError({
              status: 401,
              body: rawResponse.body,
              headers: rawResponse.headers,
            });
          }

          // Abort multipart uploads on 401 (do not silently replay 30s file uploads)
          if (isUpload) {
            throw this.normalizeError({
              status: 401,
              message: 'Authentication session expired during upload. Please log in again.',
              headers: rawResponse.headers,
            });
          }

          // Trigger single-flight refresh lock
          const isPlatform = path.startsWith('/platform/') || effectiveOptions.authScope === 'superuser';
          const newAccessToken = await this.acquireRefreshedToken(isPlatform);

          if (!newAccessToken) {
            // Refresh failed permanently
            this.sessionPort.clearSession(isPlatform);
            throw this.normalizeError({
              status: 401,
              message: 'Session expired and refresh failed. Please log in again.',
              headers: rawResponse.headers,
            });
          }

          // Token successfully refreshed: retry with the identical idempotencyKey
          continue;
        }

        // Check for 5xx transient server errors
        if (rawResponse.status >= 500 && attempt <= this.max5xxRetries) {
          await this.backoffDelay(attempt, effectiveOptions.signal);
          continue;
        }

        // If status is >= 400, normalize and throw domain AppError
        if (rawResponse.status >= 400) {
          throw this.normalizeError({
            status: rawResponse.status,
            body: rawResponse.body,
            headers: rawResponse.headers,
          });
        }

        // Unpack domain envelope
        const unpackedData = this.unpackDomainData<T>(rawResponse.body);

        return {
          data: unpackedData,
          status: rawResponse.status,
          statusText: rawResponse.statusText,
          headers: rawResponse.headers,
          requestId: rawResponse.headers['x-request-id'] || '',
        };
      } catch (err: unknown) {
        // If error is already a normalized AppError, evaluate whether to retry
        const appError = (err && (err as AppError).code)
          ? (err as AppError)
          : this.normalizeError({ rawError: err });

        // Transient network failure retry
        if (appError.code === 'NETWORK_ERROR' && attempt <= this.max5xxRetries) {
          await this.backoffDelay(attempt, effectiveOptions.signal);
          continue;
        }

        throw appError;
      }
    }
  }

  /**
   * Low-level raw dispatch across NetworkPort with header and parameter assembly.
   */
  private async executeRaw(path: string, options: RequestOptions): Promise<RawHttpResponse> {
    const isPlatform = path.startsWith('/platform/') || options.authScope === 'superuser';
    const headers: Record<string, string> = {
      'x-request-id': uuidv4(),
      ...(options.headers || {}),
    };

    // Authentication token resolution
    if (!options.skipAuth) {
      if (isPlatform) {
        const suToken = this.sessionPort.getSuperuserToken();
        if (suToken) {
          headers['authorization'] = `Bearer ${suToken}`;
        }
      } else {
        const token = this.sessionPort.getAuthToken();
        if (token) {
          headers['authorization'] = `Bearer ${token}`;
        }
        // Tenant header injection for standard tenant paths
        const tenantId = options.tenantId || this.sessionPort.getTenantId();
        if (tenantId) {
          headers['x-tenant-id'] = tenantId;
        }
      }
    }

    // Attach idempotency key if provided
    if (options.idempotencyKey) {
      headers['idempotency-key'] = String(options.idempotencyKey);
    }

    // Build URL with query parameters
    let url = path;
    if (options.params && Object.keys(options.params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    const rawRequest: RawHttpRequest = {
      url,
      method: options.method || 'GET',
      headers,
      body: options.body,
      signal: options.signal,
      timeoutMs: options.timeoutMs ?? this.defaultTimeoutMs,
      responseType: options.responseType,
    };

    return this.networkPort.send(rawRequest);
  }

  /**
   * Single-flight token refresh mutex: exactly 1 `/auth/refresh` call runs when multiple 401s occur.
   */
  private async acquireRefreshedToken(isPlatform: boolean): Promise<string | null> {
    if (this.activeRefreshPromise) {
      return this.activeRefreshPromise;
    }

    this.activeRefreshPromise = (async () => {
      try {
        const refreshToken = this.sessionPort.getRefreshToken();
        if (!refreshToken) {
          return null;
        }

        const refreshPath = isPlatform ? '/platform/auth/refresh' : '/auth/refresh';
        const refreshHeaders: Record<string, string> = {
          'content-type': 'application/json',
          'x-request-id': uuidv4(),
        };
        const tenantId = this.sessionPort.getTenantId();
        if (tenantId && !isPlatform) {
          refreshHeaders['x-tenant-id'] = tenantId;
        }

        const refreshResponse = await this.networkPort.send({
          url: refreshPath,
          method: 'POST',
          headers: refreshHeaders,
          body: JSON.stringify({ refreshToken }),
          timeoutMs: 15000,
        });

        if (refreshResponse.status !== 200 || !refreshResponse.body) {
          return null;
        }

        const body = refreshResponse.body as any;
        const newAccessToken = body?.data?.accessToken || body?.accessToken;
        const newRefreshToken = body?.data?.refreshToken || body?.refreshToken;

        if (!newAccessToken) {
          return null;
        }

        if (isPlatform) {
          this.sessionPort.saveSuperuserTokens({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          });
        } else {
          this.sessionPort.saveTenantTokens({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          });
        }

        return newAccessToken;
      } catch {
        return null;
      } finally {
        this.activeRefreshPromise = null;
      }
    })();

    return this.activeRefreshPromise;
  }

  /**
   * Exponential backoff delay with randomized jitter: t = 2^attempt * 1000ms + jitter.
   */
  private async backoffDelay(attempt: number, signal?: AbortSignal): Promise<void> {
    const baseDelay = Math.pow(2, attempt) * 1000;
    const jitter = Math.random() * 500;
    const delayMs = baseDelay + jitter;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        resolve();
      }, delayMs);

      if (signal) {
        signal.addEventListener(
          'abort',
          () => {
            clearTimeout(timer);
            reject(
              this.normalizeError({
                message: 'Request aborted by caller',
                code: 'REQUEST_ABORTED',
              })
            );
          },
          { once: true }
        );
      }
    });
  }

  /**
   * Unpacks backend envelope structures like `{ success: true, data: T }` into domain `T`.
   */
  private unpackDomainData<T>(body: unknown): T {
    if (!body || typeof body !== 'object') {
      return body as T;
    }

    const payload = body as Record<string, unknown>;

    // Handle standard { success: true, data: T } envelope
    if (payload.success === true && 'data' in payload) {
      const data = payload.data as any;
      if (payload.pagination && data && typeof data === 'object' && !('pagination' in data)) {
        try {
          Object.defineProperty(data, 'pagination', {
            value: payload.pagination,
            writable: true,
            configurable: true,
            enumerable: false,
          });
        } catch {
          // Ignore if object is not extensible
        }
      }
      return data as T;
    }

    // Handle { data: T } without success flag
    if ('data' in payload && Object.keys(payload).length === 1) {
      return payload.data as T;
    }

    return body as T;
  }

  /**
   * Normalizes arbitrary HTTP errors, network failures, or bodies into a structured AppError.
   */
  private normalizeError(params: {
    status?: number;
    message?: string;
    code?: string;
    body?: unknown;
    headers?: Record<string, string>;
    rawError?: unknown;
  }): AppError {
    const timestamp = new Date().toISOString();
    const status = params.status;

    // Handle abort errors
    if (
      params.code === 'REQUEST_ABORTED' ||
      (params.rawError && (params.rawError as Error).name === 'AbortError')
    ) {
      return {
        code: 'REQUEST_ABORTED',
        message: 'The request operation was aborted.',
        retryable: false,
        timestamp,
      };
    }

    // Handle network dropouts
    if (!status && params.rawError) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Unable to connect to the server. Please check your internet connection.',
        retryable: true,
        timestamp,
      };
    }

    const bodyObj = (params.body && typeof params.body === 'object')
      ? (params.body as Record<string, unknown>)
      : undefined;

    const extractedMessage =
      params.message ||
      (typeof bodyObj?.error === 'string' ? bodyObj.error : undefined) ||
      (typeof bodyObj?.message === 'string' ? bodyObj.message : undefined);

    let code = params.code || (typeof bodyObj?.code === 'string' ? bodyObj.code : '');
    let message = extractedMessage || 'An unexpected error occurred.';
    let retryable = false;

    switch (status) {
      case 400:
      case 422:
        code = code || 'VALIDATION_ERROR';
        message = extractedMessage || 'Invalid request. Please check your input.';
        break;
      case 401:
        code = 'AUTH_EXPIRED';
        message = extractedMessage || 'Your session has expired. Please log in again.';
        break;
      case 403:
        code = 'FORBIDDEN';
        message = extractedMessage || 'You do not have permission to perform this action.';
        break;
      case 404:
        code = 'NOT_FOUND';
        message = extractedMessage || 'The requested resource was not found.';
        break;
      case 409:
        code = 'CONFLICT';
        message = extractedMessage || 'This operation conflicts with existing server state.';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        code = code || 'SERVER_ERROR';
        message = extractedMessage || 'Server error encountered. Please try again.';
        retryable = true;
        break;
      default:
        code = code || 'UNKNOWN_ERROR';
        break;
    }

    return {
      code,
      message,
      status,
      details: bodyObj?.details as Record<string, unknown> | undefined,
      retryable,
      timestamp,
    };
  }
}
